import React, { memo, useCallback, useState } from "react";
import { TenantInfoStepProps } from "./types";
import { ReactSVG } from "react-svg";
import { useTranslate } from "helpers";
import { TextField } from "ui/components/TextField/TextField";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import CardSelect from "ui/components/CardSelect/CardSelect";
import OibTextField from "ui/components/TextField/OibTextField/OibTextField";
import OibDropdown from "ui/components/Dropdown/OibDropdown/OibDropdown";
import { SudRegResponse } from "services/api/contacts/types";
import { PhoneTextField } from "ui/components/TextField/PhoneTextField";
import { MobilePhoneTextField } from "ui/components/TextField/MobilePhoneTextField";
import { EmailTextField } from "ui/components/TextField/EmailTextField";
import { WebTextField } from "ui/components/TextField/WebTextField";
import { Button } from "ui/components/Button/Button";
import {
  AddressEditModal,
  AddressEditFormValues,
  initialAddressEditFormValues,
} from "ui/components/Map/AddressEditModal";
import {
  useHandleTextFieldChange,
  useHandleDropdownChange,
} from "services/forms/hooks";
import { timezones } from "services/api/tenants/consts";
import { TenantAddress } from "services/api/tenants/types";
import FileUploadControl from "ui/components/FileUpload/FileUploadControl/FileUploadControl";
import { FleetDocumentUpload } from "services/api/fleet/vehicles/documents/types";

import { geocodeAddress } from "ui/components/Map/geocode";

const composeAddressLine = (address: {
  line1: string | null;
  postalCode: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
}): string =>
  [address.line1, address.postalCode, address.city, address.state, address.country]
    .filter(Boolean)
    .join(", ");

const TenantInfoStep: React.FC<TenantInfoStepProps> = ({
  formValues,
  setFormValues,
  errors,
}) => {
  const { t } = useTranslate();

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);

  const handleTenantTypeChange = useCallback(
    (id: string) => {
      setFormValues((prev) => ({
        ...prev,
        tenantTypeId: id,
      }));
    },
    [setFormValues]
  );

  const timezoneOptions: DropdownOption[] = timezones.map((tz) => ({
    value: tz.id,
    text: tz.name,
  }));

  const isCompany = formValues.tenantTypeId === "C";
  const isPerson = formValues.tenantTypeId === "P";

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressValues, setAddressValues] = useState<AddressEditFormValues>(
    initialAddressEditFormValues
  );

  const handleOpenAddressModal = useCallback(() => {
    setAddressValues(
      formValues.address
        ? {
          country: formValues.address.country,
          state: formValues.address.state,
          city: formValues.address.city,
          postalCode: formValues.address.postalCode,
          line1: formValues.address.line1,
          line2: formValues.address.line2,
          latitude: formValues.address.latitude,
          longitude: formValues.address.longitude,
        }
        : initialAddressEditFormValues
    );
    setShowAddressModal(true);
  }, [formValues.address]);

  const handleCloseAddressModal = useCallback(() => {
    setShowAddressModal(false);
  }, []);

  const handleAddressChange = useCallback((values: AddressEditFormValues) => {
    setAddressValues(values);
  }, []);

  const handleOibSelect = useCallback(
    async (oib: string, sudRegData: SudRegResponse) => {
      const sudRegAddress = sudRegData.address;

      setFormValues((prev) => ({
        ...prev,
        personalIdentificationNumber: oib,
        companyName: sudRegData.name ?? prev.companyName,
        email: sudRegData.emails?.[0] ?? prev.email,
        address: sudRegAddress
          ? {
            id: prev.address?.id ?? null,
            city: sudRegAddress.city,
            country: sudRegAddress.country,
            line1: sudRegAddress.line1,
            line2: sudRegAddress.line2,
            postalCode: sudRegAddress.postalCode,
            state: sudRegAddress.state,
            description: null,
            latitude: null,
            longitude: null,
          }
          : prev.address,
        headquartersAddress: sudRegAddress
          ? composeAddressLine(sudRegAddress) || prev.headquartersAddress
          : prev.headquartersAddress,
      }));

      if (!sudRegAddress) return;

      // Iz adrese sudskog registra dohvati pin (koordinate) i dopuni dijelove
      // adrese koje sudreg nije vratio.
      const addressLine = composeAddressLine(sudRegAddress);
      const geo = await geocodeAddress(addressLine);
      if (!geo) return;

      setFormValues((prev) => ({
        ...prev,
        address: prev.address
          ? {
            ...prev.address,
            latitude: geo.latitude,
            longitude: geo.longitude,
            city: prev.address.city || geo.city || null,
            // Država mora biti engleski naziv (EUROPEAN_COUNTRIES), a sudreg
            // vraća hrvatski ("Hrvatska") — zato geokodirana vrijednost ima prednost.
            country: geo.country || prev.address.country || null,
            state: prev.address.state || geo.state || null,
            postalCode: prev.address.postalCode || geo.postalCode || null,
          }
          : prev.address,
      }));
    },
    [setFormValues]
  );

  const handleAddressSave = useCallback(() => {
    const composedAddress = composeAddressLine(addressValues);

    const structuredAddress: TenantAddress = {
      id: formValues.address?.id ?? null,
      country: addressValues.country,
      state: addressValues.state,
      city: addressValues.city,
      postalCode: addressValues.postalCode,
      line1: addressValues.line1,
      line2: addressValues.line2,
      description: null,
      latitude: addressValues.latitude ?? null,
      longitude: addressValues.longitude ?? null,
    };

    setFormValues((prev) => ({
      ...prev,
      address: structuredAddress,
      headquartersAddress: composedAddress || null,
    }));
    setShowAddressModal(false);
  }, [addressValues, formValues.address, setFormValues]);

  const handleLogoUploadSave = useCallback(
    (uploads: FleetDocumentUpload[]) => {
      setFormValues((prev) => ({
        ...prev,
        logo: uploads.length > 0 ? [uploads[0]] : [],
      }));
    },
    [setFormValues]
  );

  const handleLogoUploadDelete = useCallback(
    (_id: string) => {
      setFormValues((prev) => ({ ...prev, logo: [] }));
    },
    [setFormValues]
  );

  return (
    <>
      <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
            Osnovni podaci
          </h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Unesite osnovne podatke tenanta. Nakon unosa možete nastaviti na
            sljedeći korak.
          </p>
          <div className="border border-slate-200 ">
            {/* Tip korisnika */}
            <div className="relative border-b border-slate-200 py-4 px-4">

              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                  Tip korisnika
                </h3>
                <p className="text-xs text-slate-500">
                  Odaberite radi li se o tvrtci ili osobi
                </p>
              </div>
              <CardSelect
                options={[
                  {
                    id: "C",
                    icon: "/media/icons/company.svg",
                    title: "Tvrtka",
                    description: "Pravna osoba ili obrt",
                  },
                  {
                    id: "P",
                    icon: "/media/icons/contact.svg",
                    title: "Osoba",
                    description: "Fizička osoba",
                  },
                ]}
                value={formValues.tenantTypeId}
                onChange={handleTenantTypeChange}
                error={errors.tenantTypeId}
              />
            </div>

            {/* Osnovni / Osobni podaci */}
            {isCompany ? (
              <div className="relative border-b border-slate-200 py-4 px-4">

                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                    Osnovni podaci
                  </h3>
                  <p className="text-xs text-slate-500">
                    Unesite osnovne podatke tvrtke
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <OibDropdown
                      name="personalIdentificationNumber"
                      label={t("admin.setting.tenant.personalidentificationnumber")}
                      placeholder={t(
                        "admin.setting.tenant.personalidentificationnumber.placeholder"
                      )}
                      value={formValues.personalIdentificationNumber}
                      error={errors.personalIdentificationNumber}
                      required
                      onSudRegSelect={handleOibSelect}
                    />
                  </div>
                  <div>
                    <TextField
                      type="text"
                      name="companyName"
                      label={t("admin.setting.tenant.companyname")}
                      placeholder={t("admin.setting.tenant.companyname.placeholder")}
                      onChange={handleTextFieldChange}
                      value={formValues.companyName}
                      error={errors.companyName}
                      autoComplete="organization"
                      disableDebounce
                      required
                    />
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <TextField
                      type="text"
                      name="headquartersAddress"
                      label={t("admin.setting.tenant.headquartersaddress")}
                      placeholder={t(
                        "admin.setting.tenant.headquartersaddress.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      value={formValues.headquartersAddress}
                      error={errors.headquartersAddress}
                      autoComplete="street-address"
                      onClick={handleOpenAddressModal}
                      readOnly
                      required
                    />
                  </div>
                  <Button
                    variant="outlined"

                    onClick={handleOpenAddressModal}
                    startIcon="/media/icons/location.svg"
                  >
                    Karta
                  </Button>
                </div>
                <div className="mt-6">
                  <p className="text-xs font-medium text-slate-600 mb-2">Logotip</p>
                  <FileUploadControl
                    uploads={formValues.logo}
                    onUploadSave={handleLogoUploadSave}
                    onUploadDelete={handleLogoUploadDelete}
                    buttonText="Upload logo"
                    modalTitle="Upload logo"
                    maxFiles={1}
                  />
                </div>
              </div>
            ) : (
              <div className="relative border-b border-slate-200 py-4 px-4">

                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                    Osobni podaci
                  </h3>
                  <p className="text-xs text-slate-500">
                    Unesite osobne podatke tenanta
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <TextField
                      type="text"
                      name="firstName"
                      label={t("admin.setting.tenant.firstname")}
                      placeholder={t("admin.setting.tenant.firstname.placeholder")}
                      onChange={handleTextFieldChange}
                      value={formValues.firstName}
                      error={errors.firstName}
                      autoComplete="given-name"
                      required
                    />
                  </div>
                  <div>
                    <TextField
                      type="text"
                      name="lastName"
                      label={t("admin.setting.tenant.lastname")}
                      placeholder={t("admin.setting.tenant.lastname.placeholder")}
                      onChange={handleTextFieldChange}
                      value={formValues.lastName}
                      error={errors.lastName}
                      autoComplete="family-name"
                      required
                    />
                  </div>
                  <div>
                    <OibTextField
                      type="text"
                      name="personalIdentificationNumber"
                      label={t("admin.setting.tenant.personalidentificationnumber")}
                      placeholder={t(
                        "admin.setting.tenant.personalidentificationnumber.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      value={formValues.personalIdentificationNumber}
                      error={errors.personalIdentificationNumber}
                      required
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-xs font-medium text-slate-600 mb-2">Logotip</p>
                  <FileUploadControl
                    uploads={formValues.logo}
                    onUploadSave={handleLogoUploadSave}
                    onUploadDelete={handleLogoUploadDelete}
                    buttonText="Upload logo"
                    modalTitle="Upload logo"
                    maxFiles={1}
                  />
                </div>
              </div>
            )}

            {/* Kontakt podaci */}
            <div className="relative border-b border-slate-200 py-4 px-4">

              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                  Kontakt podaci
                </h3>
                <p className="text-xs text-slate-500">
                  Unesite kontakt podatke tenanta
                </p>
              </div>
              {/*
                Mobitel je prvi jer se češće unosi. Zvjezdice nema ni na jednom:
                obavezan je barem jedan broj, a ne oba, pa to kaže napomena ispod.
              */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <MobilePhoneTextField
                    type="tel"
                    name="mobilePhoneNumber"
                    label={t("admin.setting.tenant.mobilephonenumber")}
                    placeholder={t(
                      "admin.setting.tenant.mobilephonenumber.placeholder"
                    )}
                    onChange={handleTextFieldChange}
                    value={formValues.mobilePhoneNumber}
                    error={errors.mobilePhoneNumber}
                    autoComplete="tel"
                  />
                </div>
                <div className="flex-1">
                  <PhoneTextField
                    type="tel"
                    name="phoneNumber"
                    label={t("admin.setting.tenant.phonenumber")}
                    placeholder={t("admin.setting.tenant.phonenumber.placeholder")}
                    onChange={handleTextFieldChange}
                    value={formValues.phoneNumber}
                    error={errors.phoneNumber}
                    autoComplete="tel"
                  />
                </div>
              </div>

              <p className="text-xs text-slate-500 mt-1 mb-3">
                Obavezan je barem jedan broj.
              </p>
              <div className="flex gap-4">
                <div className="flex-1">
                  <EmailTextField
                    type="email"
                    name="email"
                    label={t("admin.setting.tenant.email")}
                    placeholder={t("admin.setting.tenant.email.placeholder")}
                    onChange={handleTextFieldChange}
                    value={formValues.email}
                    error={errors.email}
                    autoComplete="email"
                  />
                </div>
                <div className="flex-1">
                  <WebTextField
                    type="url"
                    name="web"
                    label={t("admin.setting.tenant.web")}
                    placeholder={t("admin.setting.tenant.web.placeholder")}
                    onChange={handleTextFieldChange}
                    value={formValues.web}
                    error={errors.web}
                    autoComplete="url"
                  />
                </div>
              </div>
            </div>

            {/* Vremenska zona */}
            <div className="relative py-4 px-4">

              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                  Vremenska zona
                </h3>
                <p className="text-xs text-slate-500">
                  Odaberite vremensku zonu tenanta
                </p>
              </div>
              <div className="flex gap-4">
                <div className="flex-1">
                  <Dropdown
                    name="timezone"
                    label={t("admin.setting.tenant.timezone")}
                    placeholder={t("admin.setting.tenant.timezone.placeholder")}
                    options={timezoneOptions}
                    onChange={handleDropdownChange}
                    value={formValues.timezone}
                    error={errors.timezone}
                    required
                  />
                </div>
                <div className="flex-1" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddressEditModal
        show={showAddressModal}
        title="Adresa sjedišta tvrtke"
        formValues={addressValues}
        onChange={handleAddressChange}
        onSave={handleAddressSave}
        onClose={handleCloseAddressModal}
      />
    </>
  );
};

export default memo(TenantInfoStep);
